import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import * as actions from '../../LoyaltyPage/actions';
import { distributeClientBonus } from '../../LoyaltyPage/helpers';
import { selectors } from '../../LoyaltyPage/reducer';
import type { PaymentMethod } from '../../LoyaltyPageSeparated/components/LoyaltyPaymentScreen';
import type {
  ConfirmedLoyaltyAmounts,
  StandartReceiptItem,
} from '../../LoyaltyPageSeparated/components/LoyaltyApplyScreen';
import * as Styled from './LoyaltyPaymentScreenV3.styles';
import type { PaymentHelpState } from './loyaltyHelpContent';

interface LoyaltyPaymentScreenV3Props {
  partnerPercent: number;
  isStadaBonusesCredit: boolean;
  onBack: () => void;
  showBack?: boolean;
  confirmedStandartItems?: StandartReceiptItem[];
  confirmedAmounts?: ConfirmedLoyaltyAmounts | null;
  onSelectPaymentMethod: (method: PaymentMethod) => void;
  onHelpStateChange?: (state: PaymentHelpState | null) => void;
}

const ArrowRight = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M5 12h14M13 6l6 6-6 6"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const LoyaltyPaymentScreenV3: React.FC<LoyaltyPaymentScreenV3Props> = ({
  partnerPercent,
  isStadaBonusesCredit,
  onBack,
  showBack = true,
  confirmedStandartItems = [],
  confirmedAmounts,
  onSelectPaymentMethod,
  onHelpStateChange,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const chosenReceipt = useSelector(selectors.chosenReceipt);
  const orderReceiptInfo = useSelector(selectors.orderReceiptInfo);
  const loyaltyOrderId = useSelector(selectors.loyaltyOrderId);
  const bonusUsed = useSelector(selectors.bonusUsed);
  const clientInfo = useSelector(selectors.clientInfo);
  const stadaInfo = useSelector(selectors.stadaInfo);

  const [confirmCashier, setConfirmCashier] = useState(false);
  const [submitting, setSubmitting] = useState<PaymentMethod | null>(null);
  useEffect(() => {
    onHelpStateChange?.({
      cashierConfirmation: confirmCashier,
      busy: !!submitting,
    });
  }, [onHelpStateChange, confirmCashier, submitting]);
  useEffect(() => () => onHelpStateChange?.(null), [onHelpStateChange]);

  const clientBalance = clientInfo?.balance ?? 0;
  const stadaBalance = stadaInfo?.balance ?? 0;
  const docId = chosenReceipt?.doc_id ?? orderReceiptInfo?.doc_id ?? '';
  const hasStadaItems =
    chosenReceipt?.items?.some((item) => item.stadaPercent > 0) ?? false;

  const amounts = useMemo(() => {
    if (confirmedAmounts) {
      return {
        discount: confirmedAmounts.bonusDebit,
        payable: confirmedAmounts.payable,
      };
    }
    if (confirmedStandartItems.length) {
      const payable = confirmedStandartItems.reduce(
        (sum, item) => sum + item.price * item.quantity,
        0
      );
      const original = (chosenReceipt?.items ?? []).reduce(
        (sum, item) => sum + Number(item?.total_price ?? 0),
        0
      );
      return {
        total: original,
        discount: Math.max(original - payable, 0),
        payable,
      };
    }
    const items = chosenReceipt?.items ?? [];
    const total = items.reduce(
      (sum, item) => sum + Number(item?.total_price ?? 0),
      0
    );
    const distributed = distributeClientBonus(
      items,
      bonusUsed ? clientBalance : 0,
      partnerPercent,
      bonusUsed && hasStadaItems && isStadaBonusesCredit ? stadaBalance : 0
    );
    const discount = distributed.reduce(
      (sum, item) => sum + Number(item.appliedBonus ?? 0),
      0
    );

    return { total, discount, payable: Math.max(total - discount, 0) };
  }, [
    bonusUsed,
    chosenReceipt,
    clientBalance,
    hasStadaItems,
    isStadaBonusesCredit,
    partnerPercent,
    stadaBalance,
    confirmedStandartItems,
    confirmedAmounts,
  ]);

  const formatAmount = (amount: number) =>
    Number(amount || 0).toLocaleString('ru-RU', { maximumFractionDigits: 2 });

  const chooseApp = () => {
    if (submitting) return;
    setSubmitting('app');
    dispatch(
      actions.setPartnerPaymentMethod.request({
        orderId: loyaltyOrderId,
        postEffect: () => onSelectPaymentMethod('app'),
      })
    );
  };

  const chooseCashier = () => {
    if (submitting) return;
    setSubmitting('cashier');
    dispatch(
      actions.setInPlacePaymentMethod.request({
        orderId: loyaltyOrderId,
        postEffect: () => onSelectPaymentMethod('cashier'),
      })
    );
  };

  return (
    <Styled.Screen>
      <Styled.Content>
        {!confirmCashier ? (
          <>
            <Styled.Title>{t('LoyaltyV3.HOW_TO_PAY')}</Styled.Title>
            <Styled.Summary>
              <div className="receipt">
                {t('LoyaltyV3.RECEIPT')} №{docId}
              </div>
              <strong>{formatAmount(amounts.payable)} ₸</strong>
            </Styled.Summary>

            {amounts.discount > 0 && (
              <Styled.DiscountLine>
                {t('LoyaltyV3.PHARMACY_BONUS_DISCOUNT')} −
                {formatAmount(amounts.discount)} ₸
              </Styled.DiscountLine>
            )}

            {clientInfo && (
              <Styled.MethodButton
                type="button"
                accent="beeline"
                onClick={chooseApp}
              >
                <span>
                  <strong>{t('LoyaltyV3.PAY_IN_BEELINE')}</strong>
                  <small>{t('LoyaltyV3.BEELINE_AMOUNT_HINT')}</small>
                  <small className="pharmacist-bonus-hint">
                    {t('LoyaltyV3.BEELINE_PHARMACIST_BONUS_HINT')}
                  </small>
                </span>
                <ArrowRight />
              </Styled.MethodButton>
            )}

            <Styled.MethodButton
              type="button"
              accent="cashier"
              onClick={() => setConfirmCashier(true)}
            >
              <span>
                <strong>{t('LoyaltyV3.PAY_AT_CASHIER')}</strong>
                <small>{t('LoyaltyV3.CASHIER_HINT')}</small>
              </span>
              <ArrowRight />
            </Styled.MethodButton>

            {showBack && (
              <Styled.BackButton type="button" onClick={onBack}>
                ← {t('LoyaltyV3.BACK_TO_DISCOUNTS')}
              </Styled.BackButton>
            )}
          </>
        ) : (
          <Styled.Confirmation>
            <Styled.Title>{t('LoyaltyV3.CONFIRM_CASHIER_TITLE')}</Styled.Title>
            <p>{t('LoyaltyV3.CONFIRM_CASHIER_TEXT')}</p>
            <Styled.OrderMeta>
              <div className="meta-title">
                {t('LoyaltySeparated.ORDER_INFO_TITLE')}
              </div>
              <div>
                <span>{t('LoyaltyV3.ORDER_NUMBER')}</span>
                <strong>{loyaltyOrderId}</strong>
              </div>
              <div>
                <span>{t('LoyaltyV3.RECEIPT_NUMBER')}</span>
                <strong>{docId}</strong>
              </div>
            </Styled.OrderMeta>
            <Styled.PayableBox>
              <span>{t('LoyaltyV3.CASHIER_PAYABLE')}</span>
              <strong>{formatAmount(amounts.payable)} ₸</strong>
            </Styled.PayableBox>
            <Styled.PrimaryButton
              type="button"
              onClick={chooseCashier}
              disabled={submitting === 'cashier'}
            >
              {submitting === 'cashier'
                ? t('LoyaltyV3.SAVING_PAYMENT_METHOD')
                : t('LoyaltyV3.CONFIRM_CASHIER')}
            </Styled.PrimaryButton>
            <Styled.SecondaryButton
              type="button"
              onClick={() => setConfirmCashier(false)}
              disabled={!!submitting}
            >
              ← {t('LoyaltySeparated.BACK_TO_PAYMENT_SELECT')}
            </Styled.SecondaryButton>
          </Styled.Confirmation>
        )}
      </Styled.Content>
    </Styled.Screen>
  );
};

export default LoyaltyPaymentScreenV3;
